import { TransactionActivity, Swap, TransactionStatus } from '../interfaces/transactionActivity'
import { Chain, TransactionType } from '../interfaces/common'
import { fetchEnv } from '../utils/fetchEnv'
import { isCaseSensitiveAddress } from '../utils/addressMatcher'
import { Wallet } from '../providers/WalletProvider'
import { ETH_SUPPORTED_CHAINS } from '../utils/eth'

const BASE_URL = 'https://ledger-scanner.s1.plug-wallet.com'

export enum TransactionKind {
  Transfer = 'Transfer',
  Mint = 'Mint',
  Burn = 'Burn',
  Approve = 'Approve',
}

interface Transaction {
  kind: TransactionKind
  index: number
  from: string
  to: string
  amount: number
  memo: string | null
  fee: string | null
  timestamp: number
  symbol: string
  ledger_id: string
  chain: string
  tx_id: string
  status: TransactionStatus
}

export interface PastBalanceInterval {
  period: number
  unit: 'hour' | 'day' | 'week' | 'month' | 'year'
}

interface BalanceResponse {
  [principalId: string]: number
}

export interface TokenPriceHistory {
  timestamp: number
  price: number
}

export interface NFTToken {
  index: number
  canister: string
  id?: string
  url: string
  standard: string
}

export interface NFTBalanceResponse {
  name: string
  canisterId: string
  standard: string
  tokens: NFTToken[]
  icon: string
  description: string
}

interface FetchTransactionParams {
  types: TransactionKind[]
  page?: number
  pageSize?: number
  receiver?: string
  tokenSymbol?: string
}

class LedgerScannerClient {
  private static async post(url: string, body: { [key: string]: any }) {
    const result = await fetch(`${BASE_URL}/${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, secret: fetchEnv('SCANNER_SECRET') }),
    })

    if (result.ok) {
      if (result.headers.get('Content-Type') === 'application/json') {
        return result.json()
      } else {
        return result.text()
      }
    } else {
      const errorText = (await result.text()) || 'Ledger Scanner fetch error'
      throw new Error(`HTTP ${result.status} error - ${errorText}`)
    }
  }

  private static mapTransaction(transaction: Transaction, wallet: Wallet): TransactionActivity {
    const {
      kind,
      index,
      from,
      to,
      amount,
      timestamp,
      symbol,
      ledger_id,
      memo,
      chain,
      tx_id,
      status,
    } = transaction
    let isSend
    if (!chain || chain === 'ICP') {
      isSend = [wallet.accountId, wallet.principalId].includes(from)
    } else if (chain === 'BTC') {
      isSend = wallet.btcAddress === from
    } else if (ETH_SUPPORTED_CHAINS.includes(chain.toLowerCase() as Chain)) {
      isSend = wallet.ethAddress?.toLowerCase() === from.toLowerCase()
    } else if (chain === 'SOL') {
      isSend = wallet.solAddress === from
    }

    return {
      kind,
      amount,
      memo,
      chain,
      type: isSend ? TransactionType.Send : TransactionType.Receive,
      transactionId: ['BTC', 'ETH', 'SOL', 'ARB', 'BASE'].includes(chain) ? tx_id : index,
      senderAddress: from,
      receiverAddress: to,
      currency: symbol,
      tokenLedgerId: ledger_id,
      timestamp: new Date(timestamp / 1000000),
      status,
    }
  }

  // TODO
  public static async fetchTransactions(
    wallet: Wallet,
    { types, page, pageSize, receiver, tokenSymbol }: FetchTransactionParams
  ) {
    let normalizedReceiver = receiver
    if (receiver && isCaseSensitiveAddress(receiver)) {
      normalizedReceiver = receiver.toLowerCase()
    }

    const body = {
      principal: wallet.principalId,
      account_id: wallet.accountId,
      eth: wallet.ethAddress,
      btc: wallet.btcAddress,
      sol: wallet.solAddress,
      page: page || 1,
      page_size: pageSize || 10,
      token: tokenSymbol,
      receiver: normalizedReceiver,
      types,
    }

    const path = tokenSymbol ? 'get-token-tx' : 'get-user'

    const transactions: Transaction[] = await this.post(path, body)

    return transactions.map((transaction) => this.mapTransaction(transaction, wallet))
  }

  public static async fetchSwaps(
    principal: string,
    account_id: string,
    page: number,
    page_size: number = 10
  ): Promise<Swap[]> {
    const body = {
      principal,
      account_id,
      page,
      page_size,
    }

    return this.post('get-swaps', body)
  }

  public static async refreshSwaps(principal: string): Promise<Swap[]> {
    const body = {
      principal,
    }

    return this.post('refresh-swaps', body)
  }

  public static async refreshTransactions(wallet: Wallet): Promise<Swap[]> {
    const body = {
      btc: wallet.btcAddress,
      eth: wallet.ethAddress,
      sol: wallet.solAddress,
    }

    return this.post('refresh-transactions', body)
  }

  public static async registerUser(principal: string, account_id: string): Promise<void> {
    const body = {
      principal,
      account_id,
    }

    await this.post('add-user', body)
  }

  public static async sendToken(
    principal: string,
    account_id: string,
    fcm_token: string
  ): Promise<void> {
    const body = {
      principal,
      account_id,
      fcm_token,
    }

    await this.post('add-fcm', body)
  }

  public static async fetchBalances(wallet: Wallet, ids: string[]): Promise<BalanceResponse> {
    const body = {
      owner: wallet.principalId,
      btc: wallet.btcAddress,
      sol: wallet.solAddress,
      eth: wallet.ethAddress,
      secret: fetchEnv('SCANNER_SECRET'),
      ids,
    }

    return this.post('balances/v2', body)
  }

  public static async fetchTokenPriceHistory(ledger_id: string) {
    const body = {
      ledger_id,
    }

    const response = await this.post('get-full-price-history', body)

    return Object.entries(response).map(([timestamp, price]) => ({
      timestamp: Number(timestamp) * 1000, // to miliseconds
      price: Number(price),
    }))
  }

  public static async fetchBalanceChange(wallet: Wallet, ids: string[]): Promise<number> {
    const body = {
      owner: wallet.principalId,
      btc: wallet.btcAddress,
      sol: wallet.solAddress,
      eth: wallet.ethAddress,
      secret: fetchEnv('SCANNER_SECRET'),
      ids,
    }

    return this.post('wallet-24h-change', body)
  }

  public static async fetchNFTs(
    principal: string,
    accountId: string
  ): Promise<NFTBalanceResponse[]> {
    const body = {
      principal,
      account_id: accountId,
    }

    return this.post('get-user-nfts', body)
  }
}

export default LedgerScannerClient
